import React, { useState } from "react";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Switch,
  Tooltip,
  Typography,
  Stack,
  Divider,
  Snackbar,
  Alert,
  TextField,
  InputAdornment,
  Backdrop,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { styled } from "@mui/material/styles";
import { useGetAllEmployeesDetailsQuery, useUpdateEmployeeRoleMutation } from "../../Services/APIs";

const CleanToggle = styled(Switch)(({ theme }) => ({
  width: 42,
  height: 24,
  padding: 0,
  display: "flex",
  "&:active .MuiSwitch-thumb": { width: 22 },
  "& .MuiSwitch-switchBase": {
    padding: 2,
    "&.Mui-checked": {
      transform: "translateX(18px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        backgroundColor: "#009688",
        opacity: 1,
      },
    },
  },
  "& .MuiSwitch-thumb": {
    boxShadow: "0 2px 4px 0 rgb(0 0 0 / 20%)",
    width: 20,
    height: 20,
    borderRadius: 10,
    transition: theme.transitions.create(["width"], { duration: 200 }),
  },
  "& .MuiSwitch-track": {
    borderRadius: 12,
    opacity: 1,
    backgroundColor: "#ccc",
    boxSizing: "border-box",
  },
}));

const UserManagementUI: React.FC = () => {
  const { data: employees, isLoading, refetch } = useGetAllEmployeesDetailsQuery();
  const [updateEmployeeRole] = useUpdateEmployeeRoleMutation();

  const [updating, setUpdating] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const handleSnackbarClose = () => setSnackbar({ ...snackbar, open: false });

  const handleRoleUpdate = async (employeeId: string, admin: boolean, employeeAdmin: boolean) => {
    try {
      await updateEmployeeRole({ employeeId, admin, employeeAdmin }).unwrap();
    } catch (error: any) {
      console.error("Error updating roles:", error);
      setSnackbar({
        open: true,
        message: error?.data?.message || "Failed to update roles",
        severity: "error",
      });
      throw error;
    }
  };

  const filteredEmployees = employees?.filter(
    (emp: any) =>
      emp.employeeName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3} position="relative">
      {/* 🔵 Global loading overlay */}
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: "rgba(0, 0, 0, 0.3)",
        }}
        open={updating}
      >
        <CircularProgress size={80} thickness={4} color="inherit" />
      </Backdrop>

      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h5" fontWeight={600} color="#c02424">
          Employee Access Management
        </Typography>

        <TextField
          variant="outlined"
          size="small"
          placeholder="Search employee..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            sx: { borderRadius: "30px", background: "#fff" },
          }}
          sx={{ width: 280 }}
        />
      </Stack>

      <Grid container spacing={2}>
        {filteredEmployees?.map((emp: any) => {
          const profileIcon = emp.profileIcon || emp.profileImage;

          return (
            <Grid item xs={12} sm={6} md={4} key={emp._id}>
              <Card
                elevation={1}
                sx={{
                  borderRadius: 2,
                  p: 1,
                  "&:hover": { boxShadow: 4, transition: "0.3s ease" },
                }}
              >
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                    <Avatar
                      src={profileIcon}
                      sx={{
                        width: 56,
                        height: 56,
                        bgcolor: "#c02424",
                        fontSize: 20,
                        fontWeight: 600,
                      }}
                    >
                      {emp.employeeName?.[0]?.toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {emp.employeeName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {emp.employeeEmail || "—"}
                      </Typography>
                    </Box>
                  </Stack>

                  <Divider sx={{ mb: 1 }} />

                  {/* Admin Toggle */}
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                    <Tooltip title="System Administrator">
                      <Typography variant="body2" color="text.secondary">
                        Admin
                      </Typography>
                    </Tooltip>
                    <CleanToggle
                      checked={emp.roles?.admin || false}
                      onChange={async (e) => {
                        const newValue = e.target.checked;
                        setUpdating(true);
                        try {
                          await handleRoleUpdate(emp._id, newValue, emp.roles?.employeeAdmin || false);
                          await refetch();
                          setSnackbar({
                            open: true,
                            message: "Employee roles updated successfully",
                            severity: "success",
                          });
                        } catch (err) {
                          console.error("Error updating Admin:", err);
                        } finally {
                          setUpdating(false);
                        }
                      }}
                      disabled={updating}
                    />
                  </Stack>

                  {/* Employee Admin Toggle */}
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Tooltip title="Employee Administrator">
                      <Typography variant="body2" color="text.secondary">
                        Employee Admin
                      </Typography>
                    </Tooltip>
                    <CleanToggle
                      checked={emp.roles?.employeeAdmin || false}
                      onChange={async (e) => {
                        const newValue = e.target.checked;
                        setUpdating(true);
                        try {
                          await handleRoleUpdate(emp._id, emp.roles?.admin || false, newValue);
                          await refetch();
                          setSnackbar({
                            open: true,
                            message: "Employee roles updated successfully",
                            severity: "success",
                          });
                        } catch (err) {
                          console.error("Error updating EmployeeAdmin:", err);
                        } finally {
                          setUpdating(false);
                        }
                      }}
                      disabled={updating}
                    />
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Snackbar for Success/Error */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserManagementUI;
